import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Container, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import {
  ArabicNameField,
  EnglishNameField,
  GenderField,
  MobileField,
  NationalityField,
} from "../../ProfileForm";
import {
  ArabicNameFieldSkeleton,
  EnglishNameFieldSkeleton,
  GenderFieldSkeleton,
  MobileFieldSkeleton,
  NationalityFieldSkeleton,
} from "../../ProfileForm/FormFieldSkeletons";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { Button, Spinner } from "react-bootstrap";
import {
  useGetCurrentUserProfileQuery,
  useUpdateProfileMutation,
} from "../../../services/profile/profile";
import { useBasicDataQuery } from "../../../services/basicData/basicData";

export const EditProfile = () => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors, setError },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name_ar: "",
      name_en: "",
      phone: "",
      nationality_code: "",
      gender: "",
    },
  });

  const {
    data: basicDataRes,
    isLoading: isBasicDataLoading,
    isFetching: isBasicDataFetching,
    isSuccess: isBasicDataSuccess,
  } = useBasicDataQuery();
  const {
    data: profileDataRes,
    isLoading: isProfileLoading,
    isRefetching: isProfileRefetching,
  } = useGetCurrentUserProfileQuery({
    enabled: isBasicDataSuccess,
  });
  const { mutate: updateProfileMutation, isPending } =
    useUpdateProfileMutation(setError);

  const basicData = basicDataRes?.data?.data || {};
  const profileData = profileDataRes?.data?.data ?? {};

  // Populate form with user data when both profile data and basic data are loaded
  useEffect(() => {
    if (
      profileData &&
      Object.keys(profileData).length > 0 &&
      basicData &&
      basicData.nationalities &&
      basicData.nationalities.length > 0
    ) {
      // Transform user data to match form structure
      const formData = {
        name_ar: profileData.name_ar,
        name_en: profileData.name_en,
        nationality_code: profileData.nationality_code,
        gender: profileData.gender,
        phone: profileData.phone,
      };

      reset(formData);
    }
  }, [profileData, basicData, reset]);

  const onSubmit = (data) => {
    updateProfileMutation(data);
    // Handle form submission
  };

  const profileLoading = isProfileLoading || isProfileRefetching;

  return (
    <div>
      <PageHeader title={t("CHANGE_PROFILE_DATA")}   />

      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)} className="mt-5">
          <Row>
            {profileLoading ? (
              // Show skeleton loading
              <>
                <ArabicNameFieldSkeleton />
                <EnglishNameFieldSkeleton />
                <MobileFieldSkeleton />
                <NationalityFieldSkeleton />
                <GenderFieldSkeleton />
              </>
            ) : (
              // Show actual form fields
              <>
                <ArabicNameField control={control} errors={errors} />
                <EnglishNameField control={control} errors={errors} />
                <MobileField control={control} errors={errors} watch={watch} />
                <NationalityField
                  disabled={isBasicDataLoading || isBasicDataFetching}
                  control={control}
                  errors={errors}
                  basicData={basicData}
                />
                <GenderField control={control} errors={errors} />
              </>
            )}
          </Row>

          <Button
            type="submit"
            variant="primary"
            disabled={isPending || profileLoading}
          >
            {isPending ? (
              <>
                <Spinner size="sm" />
                {t("CHANGES_SAVING")}
              </>
            ) : (
              t("SAVE_CHANGES")
            )}
          </Button>
        </form>
      </Container>
    </div>
  );
};
